/**
 * Header — top navigation bar for Inbox.
 *
 * Shows the Inbox logo/title, user name, role badge, and logout button.
 * Adapts display based on whether the user is adviser or assistant.
 */

import React from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { getUserRoles, getRoleLabel } from '../../auth/authConfig';
import { LogOut, LayoutDashboard, Settings, ArrowLeftRight, Award, FileText, Wrench, CalendarCheck } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

// Role pill colours, keyed by internal role id.
// adviser = deep blue, assistant = teal, new_business_assistant = amber.
const ROLE_BADGE_COLOURS = {
  adviser: { background: 'rgba(15, 76, 117, 0.12)', color: 'var(--cp-primary)' },
  assistant: { background: 'rgba(27, 154, 170, 0.12)', color: 'var(--cp-secondary)' },
  new_business_assistant: { background: 'rgba(244, 162, 97, 0.16)', color: 'var(--cp-accent-dark)' },
};

export default function Header() {
  const { user, role, logout, switchRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;
  const isActivePrefix = (prefix) => location.pathname === prefix || location.pathname.startsWith(prefix + '/');

  return (
    <header className="header-bar" style={styles.header}>
      <div className="header-left" style={styles.left}>
        <h1 style={styles.logo} onClick={() => navigate('/')}>
          <svg width="40" height="40" viewBox="0 0 110 110" xmlns="http://www.w3.org/2000/svg">
            <rect x="0" y="0" width="110" height="110" rx="22" fill="#0F4C75"/>
            <rect x="12" y="62" width="86" height="38" rx="6" fill="#1B9AAA" opacity="0.5"/>
            <rect x="12" y="82" width="86" height="18" rx="5" fill="#1B9AAA" opacity="0.4"/>
            <rect x="42" y="89" width="26" height="7" rx="3.5" fill="#F4A261"/>
            <rect x="24" y="18" width="62" height="72" rx="5" fill="white"/>
            <polygon points="70,18 86,18 86,34" fill="#0F4C75" opacity="0.2"/>
            <rect x="32" y="34" width="26" height="5" rx="2.5" fill="#F4A261"/>
            <rect x="32" y="46" width="38" height="3" rx="1.5" fill="#0F4C75" opacity="0.3"/>
            <rect x="32" y="55" width="32" height="3" rx="1.5" fill="#0F4C75" opacity="0.3"/>
            <rect x="32" y="64" width="28" height="3" rx="1.5" fill="#0F4C75" opacity="0.3"/>
          </svg>
          <span className="header-logo-text" style={styles.logoText}>Inbox</span>
        </h1>

        <nav className="header-nav" style={styles.nav}>
          <button
            style={{
              ...styles.navBtn,
              ...(isActive('/') ? styles.navBtnActive : {}),
            }}
            onClick={() => navigate('/')}
          >
            <LayoutDashboard size={16} />
            <span className="nav-label">Dashboard</span>
          </button>
          <button
            style={{
              ...styles.navBtn,
              ...(isActive('/reports') ? styles.navBtnActive : {}),
            }}
            onClick={() => navigate('/reports')}
          >
            <FileText size={16} />
            <span className="nav-label">Reports</span>
          </button>
          {role === 'assistant' && (
            <button
              style={{
                ...styles.navBtn,
                ...(isActive('/achievements') ? styles.navBtnActive : {}),
              }}
              onClick={() => navigate('/achievements')}
            >
              <Award size={16} />
              <span className="nav-label">Achievements</span>
            </button>
          )}
          <button
            style={{
              ...styles.navBtn,
              ...(isActive('/tools') ? styles.navBtnActive : {}),
            }}
            onClick={() => navigate('/tools')}
          >
            <Wrench size={16} />
            <span className="nav-label">Tools</span>
          </button>
          {/* Review Planner — Adviser-only surface; hidden entirely from the
              Administration Assistant and New Business Assistant roles. */}
          {role === 'adviser' && (
            <button
              style={{
                ...styles.navBtn,
                ...(isActive('/reviews') ? styles.navBtnActive : {}),
              }}
              onClick={() => navigate('/reviews')}
            >
              <CalendarCheck size={16} />
              <span className="nav-label">Reviews</span>
            </button>
          )}
          {role === 'adviser' && (
            <button
              style={{
                ...styles.navBtn,
                ...(isActivePrefix('/settings') ? styles.navBtnActive : {}),
              }}
              onClick={() => navigate('/settings')}
            >
              <Settings size={16} />
              <span className="nav-label">Settings</span>
            </button>
          )}
        </nav>
      </div>

      <div style={styles.right}>
        <div style={styles.userInfo}>
          <span className="header-user-name" style={styles.userName}>{user?.name}</span>
          <span style={{
            ...styles.roleBadge,
            ...(ROLE_BADGE_COLOURS[role] || ROLE_BADGE_COLOURS.assistant),
          }}>
            {getRoleLabel(role)}
          </span>
        </div>
        {getUserRoles(user?.email).length > 1 && (
          <button
            style={styles.switchBtn}
            onClick={() => { switchRole(); navigate('/'); }}
            title="Switch role"
          >
            <ArrowLeftRight size={14} />
          </button>
        )}
        <button style={styles.logoutBtn} onClick={async () => { await logout(); navigate('/login'); }} title="Sign out">
          <LogOut size={16} />
        </button>
      </div>
    </header>
  );
}

const styles = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 1.5rem',
    height: '64px',
    background: 'var(--cp-surface)',
    borderBottom: '1px solid var(--cp-border)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    margin: 0,
  },
  logoText: {
    fontSize: '1.15rem',
    fontWeight: 700,
    color: 'var(--cp-primary)',
    letterSpacing: '-0.01em',
  },
  nav: {
    display: 'flex',
    gap: '0.25rem',
  },
  navBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    padding: '0.4rem 0.75rem',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--cp-text-muted)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  navBtnActive: {
    color: 'var(--cp-primary)',
    background: 'rgba(15, 76, 117, 0.08)',
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  userName: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--cp-text)',
  },
  roleBadge: {
    padding: '0.15rem 0.5rem',
    borderRadius: '999px',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  switchBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    color: 'var(--cp-text-muted)',
    background: 'transparent',
    border: '1px solid var(--cp-border)',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  logoutBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    color: 'var(--cp-text-muted)',
    background: 'transparent',
    border: '1px solid var(--cp-border)',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
};
